import { ChangeDetectorRef, Component, Inject, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { toIso8601String } from '@bcl/common-ui';
import {
  addDays,
  SelectOption,
  today,
  SnackbarService,
} from '@bcl/design-system';
import {
  AddProductDialogData,
  AddProductResult,
  AddProductService,
} from '../visit-detail-products/visit-detail-products.service';
import { DocumentsListApiService } from '../visit-documents/visit-documents.service';

@Component({
  selector: 'app-visit-consumables-dialog',
  templateUrl: './visit-consumables-dialog.component.html',
  styleUrls: ['./visit-consumables-dialog.component.scss'],
})
export class VisitConsumablesDialogComponent implements OnInit {
  public actionType: string;
  public form: FormGroup;
  public productOptions: SelectOption[] = [];
  public supplierOptions: SelectOption[] = [];
  public selectedProduct: any = null;
  public defaultCar: number | null = null;
  public downloadingMsds = false;
  private allProducts: any[] = [];
  private minExpirationDate: Date = addDays(today(), 1);

  constructor(
    fb: FormBuilder,
    @Inject(MAT_DIALOG_DATA) public readonly data: AddProductDialogData,
    private readonly dialogRef: MatDialogRef<
      VisitConsumablesDialogComponent,
      AddProductResult
    >,
    private readonly addProductService: AddProductService,
    private readonly changeDet: ChangeDetectorRef,
    private readonly documentsApiService: DocumentsListApiService,
    private readonly snackbarService: SnackbarService
  ) {
    this.actionType = data.operationType;
    this.defaultCar = data.defaultCar;

    this.form = fb.group({
      warehouseId: [null, Validators.required],
      productId: [null, Validators.required],
      supplierId: null,
      amount: [null, Validators.required],
      allowToDisappear: [false],
    });
  }
  public get canSubmit() {
    return this.form.touched && this.form.valid;
  }

  public get hasMsdsReference() {
    return (
      this.selectedProduct?.referenceID != null &&
      this.selectedProduct?.referenceID !== undefined
    );
  }

  public get canAllowToDisappear() {
    return this.selectedProduct?.allowToDisappear === true;
  }

  public get minExpirationDateString() {
    return toIso8601String(this.minExpirationDate);
  }

  ngOnInit(): void {
    if (this.actionType !== 'storeInWarehouse') {
      this.form.get('warehouseId')?.valueChanges.subscribe(() => {
        this.form.get('productId')?.setValue(null);
        this.productOptions = [];
        this.getProductsForWarehouse(this.form.get('warehouseId')?.value);
      });

      this.form.get('productId')?.valueChanges.subscribe((productId) => {
        this.selectedProduct = this.allProducts.find(
          (product) => product.rowId === productId
        );
        this.form.get('supplierId')?.setValue(this.selectedProduct?.supplierId);
        this.form.get('amount')?.setValue(1);
        this.form.get('allowToDisappear')?.setValue(false);
      });
      if (
        this.defaultCar &&
        (this.actionType === 'add' ||
          this.actionType === 'storeInWarehouse' ||
          this.actionType === 'replace')
      ) {
        this.form.get('warehouseId')?.patchValue(this.defaultCar?.toString());
      }
    } else {
      this.form.controls.productId.setValidators(null);
      this.form.controls.amount.setValidators(null);
      this.form.get('warehouseId')?.setValue(this.defaultCar?.toString());
      this.form.markAsTouched();
    }
  }

  public submit() {
    const { expirationDate, ...rest } = this.form.getRawValue();
    this.dialogRef.close({
      ...rest,
      productId: this.selectedProduct?.productId,
      expirationDate: expirationDate ? new Date(expirationDate) : null,
      unitId: this.selectedProduct?.unit,
      action: this.actionType,
      visitProductId: this.data.product?.id,
      allowToDisappear: this.form.get('allowToDisappear')?.value || false,
    });
  }

  public downloadMsds() {
    if (!this.selectedProduct?.referenceID) {
      return;
    }

    this.downloadingMsds = true;

    this.documentsApiService
      .downloadFile(this.selectedProduct.referenceID)
      .subscribe({
        next: (blob) => {
          const fileName =
            this.selectedProduct.documentName ||
            `MSDS_${this.selectedProduct.name}`;
          this.documentsApiService.downloadFileToDisk(blob, fileName);

          this.snackbarService.open(
            'green',
            'DOCUMENTS_LIST.MESSAGES.DOWNLOAD_DOCUMENT_SUCCESS'
          );

          this.downloadingMsds = false;
        },
        error: () => {
          this.snackbarService.open(
            'red',
            'DOCUMENTS_LIST.MESSAGES.DOWNLOAD_DOCUMENT_ERROR'
          );

          this.downloadingMsds = false;
        },
      });
  }

  getProductsForWarehouse(warehouseId: number | null) {
    if (warehouseId) {
      this.addProductService
        .getProductsForWarehouse(warehouseId)
        .subscribe((products: any) => {
          this.allProducts = products?.elements ?? [];
          let counter = 0;
          this.allProducts.forEach((product) => {
            product.rowId = counter++;
          });
          this.productOptions = products?.elements.map((product: any) => ({
            value: product.rowId,
            label:
              product.name +
              (product.supplier ? ' | ' + product.supplier : '') +
              ' | ' +
              product.amount +
              ' ' +
              product.unit,
          }));
        });
    } else {
      this.productOptions = [];
    }
  }

  getSuppliersForProduct(productId: number | null) {
    if (productId) {
      this.addProductService
        .getSuppliersForProduct(productId)
        .subscribe((suppliers: any) => {
          this.supplierOptions = suppliers?.elements.map((supplier: any) => ({
            value: supplier.supplierId,
            label: supplier.supplierName,
          }));
        });
    }
  }
}
